// ============================================================================
// LOAN MAPPING HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { loanMappingService } from 'api/services/loanMappingService';
import type { LoanMapping, LoanMappingDetail } from 'api/types/loanMapping.types';
import type { ID } from 'api/types';
import { useDataChangeListener } from './useDataSync';

const DATA_KEY = 'loan-mappings';

export const useLoanMappings = () => {
  const [data, setData] = useState<LoanMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await loanMappingService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useDataChangeListener(DATA_KEY, fetchMappings);

  return { data, loading, error, refetch: fetchMappings };
};

export const useLoanMappingDetail = () => {
  const [data, setData] = useState<LoanMappingDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async (id: ID): Promise<LoanMappingDetail | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await loanMappingService.getById(id);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
};
